import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Star, Phone, Mail, Trash2, Clock, CheckCircle } from 'lucide-react';

const DoorHanger = () => {
  const phoneNumber = "403-801-4638";
  const websiteURL = "https://yycpickupdelivery.ca";
  
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
      {/* Print-ready door hanger - 4.25" x 11" */}
      <div className="relative w-[425px] h-[1100px] bg-white shadow-2xl overflow-hidden">
        
        {/* Door Hanger Hook Cutout */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-16 bg-white">
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-14 h-14 bg-gray-100 rounded-full border-4 border-white"></div>
        </div>

        {/* Main Content */}
        <div className="relative pt-20 px-6 h-full flex flex-col">
          
          {/* Logo Section */}
          <div className="text-center mb-4">
            <div className="inline-block bg-black px-6 py-3 rounded-lg mb-3">
              <h1 className="text-3xl font-bold text-white">YYC PICK-UP</h1>
              <p className="text-sm text-gray-300 tracking-wider">DELIVERY & HAULING SERVICES LTD.</p>
            </div>
          </div>

          {/* Main Message */}
          <div className="text-center mb-6">
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-4 px-4 rounded-xl shadow-lg mb-4">
              <h2 className="text-3xl font-black mb-1">JUNK REMOVAL</h2>
              <p className="text-lg font-semibold">Fast • Reliable • Affordable</p>
            </div>
            <p className="text-xl font-bold text-gray-800 mb-2">Calgary & Surrounding Areas</p>
          </div>

          {/* Key Benefits */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6 border-2 border-gray-200">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-sm text-gray-900">Same Day</p>
                  <p className="text-xs text-gray-600">Service Available</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-sm text-gray-900">Fully Insured</p>
                  <p className="text-xs text-gray-600">& Registered</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-sm text-gray-900">Local Business</p>
                  <p className="text-xs text-gray-600">Alberta Owned</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-sm text-gray-900">Fair Pricing</p>
                  <p className="text-xs text-gray-600">No Hidden Fees</p>
                </div>
              </div>
            </div>
          </div>

          {/* What We Remove */}
          <div className="mb-4">
            <h3 className="text-center text-lg font-bold text-gray-900 mb-3">What We Remove</h3>
            <div className="grid grid-cols-2 gap-2 text-sm mb-2">
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="text-gray-700">Old Furniture</span>
              </div>
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="text-gray-700">Appliances</span>
              </div>
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="text-gray-700">Electronics</span>
              </div>
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="text-gray-700">Mattresses</span>
              </div>
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="text-gray-700">Renovation Debris</span>
              </div>
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span className="text-gray-700">Estate Cleanouts</span>
              </div>
            </div>
            <p className="text-center text-sm font-semibold text-gray-700">...and more!</p>
          </div>

          {/* 5 Star Reviews */}
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-3 mb-4">
            <div className="flex items-center justify-center gap-1 mb-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-center text-sm font-bold text-gray-900 mb-1">5-STAR RATED SERVICE</p>
            <p className="text-center text-xs text-gray-700 italic">"Quick response and fair pricing!"</p>
            <p className="text-center text-xs text-gray-600 mt-0.5">- Mike T.</p>
          </div>

          {/* Phone Number - Primary CTA */}
          <div className="bg-black text-white rounded-xl p-4 mb-4 text-center">
            <p className="text-xs font-semibold mb-2 tracking-wide">TEXT FOR A QUICK QUOTE</p>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Phone className="w-6 h-6" />
              <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} className="text-3xl font-black tracking-tight">
                {phoneNumber}
              </a>
            </div>
            <div className="flex items-center justify-center gap-2 mt-2 text-xs text-gray-300">
              <Clock className="w-3 h-3" />
              <span>Available 24/7</span>
            </div>
          </div>

          {/* QR Code */}
          <div className="bg-white rounded-xl p-3 text-center mb-6">
            <p className="text-sm font-bold text-gray-900 mb-2">SCAN FOR MORE INFO</p>
            <div className="flex justify-center mb-2">
              <QRCodeSVG 
                value={websiteURL}
                size={110}
                level="H"
                includeMargin={false}
              />
            </div>
            <p className="text-xs text-gray-600 font-medium">yycpickupdelivery.ca</p>
          </div>

          {/* Bottom Tagline */}
          <div className="text-center py-3 border-t-2 border-gray-200 mb-2">
            <p className="text-xs font-bold text-red-600 uppercase tracking-wider">Alberta Strong • Locally Owned</p>
          </div>
        </div>
      </div>

      {/* Print Button (only shows on screen, not in print) */}
      <button 
        onClick={() => window.print()} 
        className="fixed bottom-8 right-8 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-red-700 transition-colors print:hidden font-semibold"
      >
        Print Door Hanger
      </button>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: 4.25in 11in;
            margin: 0;
          }
          .bg-gray-100 {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DoorHanger;
